/**
 * Os dados do agendamento: o passo 3 da "Nova consulta", com os campos do
 * "Horário Vago" do SISAC (fase 2 da agenda da clínica).
 *
 * ── O paciente ───────────────────────────────────────────────────────────
 *
 * Vinculado ao contato, aparece como "[código] - nome", e o cadastro dele
 * pré-preenche o resto. Sem vínculo, o bloco "Paciente no SISAC" (que vem por
 * prop, para o widget não depender de como se procura) busca pelo CPF, nome ou
 * código e vincula. Quem o SISAC não tem pode ir sem cadastro, só pelo nome.
 *
 * ── As listas ────────────────────────────────────────────────────────────
 *
 * Convênio, plano, origem e local vêm das operações da clínica. Sem a operação
 * ligada, o campo vira texto (o código, como o SISAC grava).
 *
 * ── O endereço pelo CEP (#1187) ──────────────────────────────────────────
 *
 * O CEP completo pergunta ao servidor (`POST /v1/cep/consulta`) e preenche
 * endereço, bairro, cidade e UF. Só onde está vazio, ou onde o CEP anterior
 * preencheu: o que a pessoa digitou fica. Sem resposta, preenche-se à mão.
 */

import { useEffect, useState, type ChangeEvent, type ReactNode } from 'react';
import type { CampoComPadrao } from '@cliqcrm/contracts';
import { useEnderecoDoCep } from '@/shared/api/consultas';
import {
  Botao,
  Campo,
  CampoArea,
  CampoData,
  CampoSelecao,
  Interruptor,
  hojeLocal,
} from '@/shared/ui';
import { Carregando } from '@/shared/ui/estados/estados';
import {
  SEXOS,
  TAMANHOS_DO_ENDERECO,
  TIPOS_DE_AGENDAMENTO,
  idadeEmPalavras,
  maiusculas,
  mascararCep,
  type CampoDoFormulario,
  type FormularioDoAgendamento,
  type OpcaoDaLista,
} from '../lib/formulario-do-agendamento';
import { cpfProtegido, mascararCpf } from '@/entities/saude/paciente';
import { ESPERA_DO_SISAC } from '../lib/consultas-na-tela';
import type {
  ListaDoFormulario,
  OpcaoDaAgenda,
  PacienteDaConsulta,
} from '../lib/consultas-na-tela';
import './dados-do-agendamento.css';

type Erros = Partial<Record<CampoDoFormulario | 'paciente', string>>;

/**
 * O texto em maiúsculas enquanto se digita, como o SISAC guarda (#1187). A
 * posição do cursor fica: corrigir uma letra no meio não o joga para o fim.
 */
function emMaiusculas(
  evento: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  aplicar: (valor: string) => void,
): void {
  const campo = evento.currentTarget;
  const valor = maiusculas(campo.value);
  if (valor !== campo.value) {
    const { selectionStart, selectionEnd } = campo;
    campo.value = valor;
    if (selectionStart !== null && selectionEnd !== null) {
      campo.setSelectionRange(selectionStart, selectionEnd);
    }
  }
  aplicar(valor);
}

/** O que o CEP preenche. */
const CAMPOS_DO_ENDERECO = ['endereco', 'bairro', 'cidade', 'uf'] as const;
type CampoDoEndereco = (typeof CAMPOS_DO_ENDERECO)[number];

export function DadosDoAgendamento({
  formulario,
  erros,
  listas,
  paciente,
  buscaDoPaciente,
  especialidade,
  especialidades,
  hoje,
  travado,
  ocultos = [],
  aoMudar,
}: {
  formulario: FormularioDoAgendamento;
  /** Os erros a mostrar: vazio até a primeira tentativa de agendar. */
  erros: Erros;
  listas: {
    readonly convenios: ListaDoFormulario;
    readonly planos: ListaDoFormulario;
    readonly origens: ListaDoFormulario;
    readonly locais: ListaDoFormulario;
  };
  paciente: PacienteDaConsulta;
  /** O bloco que procura e vincula o paciente no SISAC; nulo sem cliente na oportunidade. */
  buscaDoPaciente: ReactNode;
  especialidade: string;
  /**
   * As especialidades do médico escolhido (#1187): com mais de uma, a da
   * consulta se troca aqui. Sem elas, o campo só mostra a escolhida.
   */
  especialidades?: {
    readonly valor: string;
    readonly opcoes: readonly OpcaoDaAgenda[];
    readonly aoEscolher: (id: string) => void;
  };
  hoje: Date;
  travado: boolean;
  /**
   * Os campos que a organização escondeu (#1187): vão sempre com o padrão. Com
   * erro (o padrão não serve), o campo volta a aparecer para a pessoa corrigir.
   */
  ocultos?: readonly CampoComPadrao[];
  aoMudar: (mudanca: Partial<FormularioDoAgendamento>) => void;
}): ReactNode {
  const aparece = (campo: CampoComPadrao) => !ocultos.includes(campo) || erros[campo] !== undefined;
  const idade = idadeEmPalavras(formulario.nascimento, hoje);
  const vinculado = paciente.situacao === 'vinculado' ? paciente.paciente : null;
  // O dia de hoje no relógio de quem está na tela, e não o de Greenwich.
  const hojeDia = hojeLocal(hoje);

  /*
   * O endereço que o CEP trouxe, aplicado quando chega. É um efeito, e não um
   * ajuste no render: quem guarda o formulário é a tela de cima. `doCep` guarda
   * o que o CEP preencheu, para o CEP seguinte trocar (ou limpar) só o que
   * ainda é dele; o que a pessoa digitou fica.
   */
  const busca = useEnderecoDoCep(formulario.cep);
  const digitosDoCep = formulario.cep.replace(/\D/g, '');
  const [aplicado, definirAplicado] = useState<string | null>(null);
  const [doCep, definirDoCep] = useState<Partial<Record<CampoDoEndereco, string>>>({});
  useEffect(() => {
    const dados = busca.data;
    if (!dados || dados.cep !== digitosDoCep || aplicado === digitosDoCep) return;
    definirAplicado(digitosDoCep);
    const doProvedor: Record<CampoDoEndereco, string> = {
      endereco: dados.logradouro ?? '',
      bairro: dados.bairro ?? '',
      cidade: dados.cidade ?? '',
      uf: dados.uf ?? '',
    };
    const mudanca: Partial<Record<CampoDoEndereco, string>> = {};
    const preenchido: Partial<Record<CampoDoEndereco, string>> = {};
    for (const campo of CAMPOS_DO_ENDERECO) {
      const atual = formulario[campo];
      const novo = maiusculas(doProvedor[campo]).slice(0, TAMANHOS_DO_ENDERECO[campo]);
      // Vazio, ou ainda o que o CEP anterior pôs: é do CEP, e o novo manda (até para limpar).
      if (atual === '' || atual === doCep[campo]) {
        if (novo !== atual) mudanca[campo] = novo;
        if (novo !== '') preenchido[campo] = novo;
      }
    }
    definirDoCep(preenchido);
    if (Object.keys(mudanca).length > 0) aoMudar(mudanca);
  }, [busca.data, digitosDoCep, aplicado, doCep, formulario, aoMudar]);
  const ajudaDoCep =
    digitosDoCep.length !== 8
      ? 'O endereço vem pelo CEP.'
      : busca.isFetching
        ? 'Buscando o endereço…'
        : busca.isError
          ? 'A busca do CEP não respondeu. Preencha o endereço.'
          : busca.data === null
            ? 'CEP não encontrado. Preencha o endereço.'
            : 'Endereço do CEP. Confira e complete.';

  return (
    <div className="dados-do-agendamento">
      <div className="dados-do-agendamento__paciente" role="group" aria-labelledby="dados-paciente">
        <div className="dados-do-agendamento__cab">
          <h5 className="dados-do-agendamento__titulo" id="dados-paciente">
            Paciente
          </h5>
          {!vinculado && (
            <Botao
              variante="fantasma"
              miudo
              desabilitado={travado}
              motivo={ESPERA_DO_SISAC}
              aria-pressed={formulario.semCadastro}
              onClick={() => aoMudar({ semCadastro: !formulario.semCadastro })}
            >
              {formulario.semCadastro ? 'Procurar no SISAC' : 'Agendar sem cadastro'}
            </Botao>
          )}
        </div>
        {paciente.situacao === 'lendo' ? (
          <Carregando forma="linha" linhas={1} rotulo="Lendo o paciente no SISAC…" />
        ) : vinculado ? (
          <p className="dados-do-agendamento__vinculado">
            <strong>
              {vinculado.codigo} - {vinculado.nome}
            </strong>
            {vinculado.finalDoCpf ? (
              <span className="dados-do-agendamento__meta">
                CPF {cpfProtegido(vinculado.finalDoCpf)}
              </span>
            ) : null}
          </p>
        ) : formulario.semCadastro ? (
          <Campo
            id="dados-nome"
            rotulo="Nome completo do paciente"
            autoComplete="off"
            value={formulario.nome}
            erro={erros.nome}
            ajuda="O paciente vai sem cadastro no SISAC, só com estes dados."
            onChange={(e) => emMaiusculas(e, (nome) => aoMudar({ nome }))}
          />
        ) : (
          <>
            {buscaDoPaciente ?? (
              <p className="dados-do-agendamento__nota">
                A busca do paciente no SISAC não está disponível aqui. Agende sem cadastro.
              </p>
            )}
            {erros.paciente && (
              <p className="dados-do-agendamento__erro" role="alert">
                {erros.paciente}
              </p>
            )}
          </>
        )}
      </div>

      <div className="dados-do-agendamento__campos">
        {especialidades && especialidades.opcoes.length > 1 ? (
          <CampoSelecao
            id="dados-especialidade"
            rotulo="Especialidade da consulta"
            value={especialidades.valor}
            disabled={travado}
            onChange={(e) => especialidades.aoEscolher(e.currentTarget.value)}
          >
            {especialidades.opcoes.map((opcao) => (
              <option key={opcao.id} value={opcao.id}>
                {opcao.nome}
              </option>
            ))}
          </CampoSelecao>
        ) : (
          <Campo
            id="dados-especialidade"
            rotulo="Especialidade da consulta"
            value={especialidade}
            readOnly
          />
        )}
        {aparece('local') && (
          <Lista
            id="dados-local"
            rotulo="Local"
            lista={listas.locais}
            valor={formulario.local}
            erro={erros.local}
            travado={travado}
            aoEscolher={(local) => aoMudar({ local })}
          />
        )}
        {aparece('origem') && (
          <Lista
            id="dados-origem"
            rotulo="Origem"
            ajuda="Por onde o pedido chegou."
            lista={listas.origens}
            valor={formulario.origem}
            erro={erros.origem}
            travado={travado}
            aoEscolher={(origem) => aoMudar({ origem })}
          />
        )}
        <CampoSelecao
          id="dados-tipo"
          rotulo="Tipo"
          value={formulario.tipo}
          erro={erros.tipo}
          disabled={travado}
          onChange={(e) => aoMudar({ tipo: e.currentTarget.value })}
        >
          {TIPOS_DE_AGENDAMENTO.map((tipo) => (
            <option key={tipo.valor} value={tipo.valor}>
              {tipo.rotulo}
            </option>
          ))}
        </CampoSelecao>
        <CampoData
          id="dados-nascimento"
          rotulo="Nascimento"
          valor={formulario.nascimento}
          erro={erros.nascimento}
          max={hojeDia}
          hoje={hojeDia}
          desabilitado={travado}
          aoMudar={(nascimento) => aoMudar({ nascimento })}
        />
        <Campo
          id="dados-idade"
          rotulo="Idade"
          value={idade ?? ''}
          placeholder="Calculada pelo nascimento"
          readOnly
        />
        <CampoSelecao
          id="dados-sexo"
          rotulo="Sexo (opcional)"
          value={formulario.sexo}
          disabled={travado}
          onChange={(e) => aoMudar({ sexo: e.currentTarget.value })}
        >
          <option value="">Não informado</option>
          {SEXOS.map((sexo) => (
            <option key={sexo.valor} value={sexo.valor}>
              {sexo.rotulo}
            </option>
          ))}
        </CampoSelecao>
        <Campo
          id="dados-cep"
          rotulo="CEP"
          inputMode="numeric"
          autoComplete="off"
          placeholder="00000-000"
          value={formulario.cep}
          erro={erros.cep}
          ajuda={ajudaDoCep}
          ajudaEmbaixo
          disabled={travado}
          onChange={(e) => aoMudar({ cep: mascararCep(e.currentTarget.value) })}
        />
        <Campo
          id="dados-endereco"
          rotulo="Endereço"
          autoComplete="off"
          maxLength={TAMANHOS_DO_ENDERECO.endereco}
          value={formulario.endereco}
          erro={erros.endereco}
          disabled={travado}
          onChange={(e) => emMaiusculas(e, (endereco) => aoMudar({ endereco }))}
        />
        <Campo
          id="dados-numero"
          rotulo="Número"
          autoComplete="off"
          maxLength={TAMANHOS_DO_ENDERECO.numero}
          value={formulario.numero}
          erro={erros.numero}
          disabled={travado}
          onChange={(e) => emMaiusculas(e, (numero) => aoMudar({ numero }))}
        />
        <Campo
          id="dados-bairro"
          rotulo="Bairro"
          autoComplete="off"
          maxLength={TAMANHOS_DO_ENDERECO.bairro}
          value={formulario.bairro}
          erro={erros.bairro}
          disabled={travado}
          onChange={(e) => emMaiusculas(e, (bairro) => aoMudar({ bairro }))}
        />
        <Campo
          id="dados-cidade"
          rotulo="Cidade"
          autoComplete="off"
          maxLength={TAMANHOS_DO_ENDERECO.cidade}
          value={formulario.cidade}
          erro={erros.cidade}
          disabled={travado}
          onChange={(e) => emMaiusculas(e, (cidade) => aoMudar({ cidade }))}
        />
        <Campo
          id="dados-uf"
          rotulo="UF"
          autoComplete="off"
          maxLength={TAMANHOS_DO_ENDERECO.uf}
          value={formulario.uf}
          erro={erros.uf}
          disabled={travado}
          onChange={(e) => emMaiusculas(e, (uf) => aoMudar({ uf }))}
        />
        {aparece('convenio') && (
          <Lista
            id="dados-convenio"
            rotulo="Convênio"
            lista={listas.convenios}
            valor={formulario.convenio}
            erro={erros.convenio}
            travado={travado}
            aoEscolher={(convenio) => aoMudar({ convenio, plano: '' })}
          />
        )}
        {aparece('plano') && (
          <Lista
            id="dados-plano"
            rotulo="Plano"
            lista={listas.planos}
            valor={formulario.plano}
            erro={erros.plano}
            travado={travado || formulario.convenio === ''}
            motivoVazio="Escolha o convênio primeiro."
            aoEscolher={(plano) => aoMudar({ plano })}
          />
        )}
        <Campo
          id="dados-matricula"
          rotulo="Matrícula"
          ajuda="A carteirinha do convênio; no particular, SN."
          ajudaEmbaixo
          autoComplete="off"
          value={formulario.matricula}
          disabled={travado}
          onChange={(e) => emMaiusculas(e, (matricula) => aoMudar({ matricula }))}
        />
        {!vinculado && (
          <Campo
            id="dados-cpf"
            rotulo="CPF (opcional)"
            inputMode="numeric"
            autoComplete="off"
            placeholder="000.000.000-00"
            value={formulario.cpf}
            erro={erros.cpf}
            disabled={travado}
            onChange={(e) => aoMudar({ cpf: mascararCpf(e.currentTarget.value) })}
          />
        )}
        <Campo
          id="dados-rg"
          rotulo="RG (opcional)"
          autoComplete="off"
          maxLength={TAMANHOS_DO_ENDERECO.rg}
          value={formulario.rg}
          disabled={travado}
          onChange={(e) => emMaiusculas(e, (rg) => aoMudar({ rg }))}
        />
        <Campo
          id="dados-orgao-emissor"
          rotulo="Órgão emissor (opcional)"
          placeholder="Ex.: SSP/MA"
          autoComplete="off"
          maxLength={TAMANHOS_DO_ENDERECO.orgaoEmissor}
          value={formulario.orgaoEmissor}
          disabled={travado}
          onChange={(e) => emMaiusculas(e, (orgaoEmissor) => aoMudar({ orgaoEmissor }))}
        />
        <Campo
          id="dados-whatsapp"
          rotulo="WhatsApp"
          inputMode="tel"
          autoComplete="off"
          value={formulario.whatsapp}
          erro={erros.whatsapp}
          ajuda="Vem do telefone do contato."
          ajudaEmbaixo
          disabled={travado}
          onChange={(e) => aoMudar({ whatsapp: e.currentTarget.value })}
        />
        <Campo
          id="dados-celular"
          rotulo="Celular"
          inputMode="tel"
          autoComplete="off"
          value={formulario.celular}
          erro={erros.celular}
          disabled={travado}
          onChange={(e) => aoMudar({ celular: e.currentTarget.value })}
        />
        <Campo
          id="dados-email"
          rotulo="E-mail"
          type="email"
          autoComplete="off"
          value={formulario.email}
          erro={erros.email}
          disabled={travado}
          onChange={(e) => aoMudar({ email: e.currentTarget.value })}
        />
      </div>

      <div className="dados-do-agendamento__opcoes">
        <Interruptor
          rotulo="Telemedicina"
          ligado={formulario.telemedicina}
          desabilitado={travado}
          motivo={ESPERA_DO_SISAC}
          aoAlternar={() => aoMudar({ telemedicina: !formulario.telemedicina })}
        />
        <Interruptor
          rotulo="Urgente"
          ligado={formulario.urgente}
          desabilitado={travado}
          motivo={ESPERA_DO_SISAC}
          aoAlternar={() => aoMudar({ urgente: !formulario.urgente })}
        />
      </div>
      <CampoArea
        id="dados-observacao"
        rotulo="Observação"
        rows={2}
        value={formulario.observacao}
        disabled={travado}
        onChange={(e) => emMaiusculas(e, (observacao) => aoMudar({ observacao }))}
      />
    </div>
  );
}

/** Um campo de lista do SISAC: escolha quando a operação está ligada, texto quando não. */
function Lista({
  id,
  rotulo,
  ajuda,
  lista,
  valor,
  erro,
  travado,
  motivoVazio,
  aoEscolher,
}: {
  id: string;
  rotulo: string;
  ajuda?: string;
  lista: ListaDoFormulario;
  valor: string;
  erro?: string;
  travado: boolean;
  motivoVazio?: string;
  aoEscolher: (valor: string) => void;
}): ReactNode {
  if (lista.situacao === 'desligada') {
    return (
      <Campo
        id={id}
        rotulo={rotulo}
        ajuda={ajuda ?? 'O código, como o SISAC grava.'}
        ajudaEmbaixo
        autoComplete="off"
        value={valor}
        erro={erro}
        disabled={travado}
        onChange={(e) => aoEscolher(e.currentTarget.value)}
      />
    );
  }
  const opcoes: readonly OpcaoDaLista[] = lista.situacao === 'pronta' ? lista.opcoes : [];
  const vazia = lista.situacao === 'pronta' && opcoes.length === 0;
  return (
    <CampoSelecao
      id={id}
      rotulo={rotulo}
      ajuda={lista.situacao === 'erro' ? undefined : ajuda}
      ajudaEmbaixo
      value={valor}
      erro={erro ?? (lista.situacao === 'erro' ? lista.recado : undefined)}
      disabled={travado || lista.situacao !== 'pronta'}
      onChange={(e) => aoEscolher(e.currentTarget.value)}
    >
      <option value="">
        {lista.situacao === 'carregando'
          ? 'Carregando…'
          : vazia
            ? (motivoVazio ?? 'Nada para escolher')
            : 'Escolha'}
      </option>
      {opcoes.map((opcao) => (
        <option key={opcao.valor} value={opcao.valor}>
          {opcao.rotulo}
        </option>
      ))}
    </CampoSelecao>
  );
}
